import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { HeroImageSliceData } from '@adapters/prismic/types/slices';
import Container from '@components/global/Container';
import Button from '@components/ui/Button';
import Heading from '@components/ui/Heading';
import BannerImage from '@components/ui/BannerImage';
import { eePromoImpHandler, setPromoDetailsCookie } from '@utils/gtm';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { useLocale } from '@hooks/useLocale';
import styles from './HeroImage.module.scss';

const HeroImage = ({
  image,
  link,
  heading,
  button_text,
  content,
  trackPromos,
  pageType,
  sliceIndex,
}: HeroImageSliceData): ReactElement => {
  const locale = useLocale();
  const ref = useRef<any>(null);
  const isVisible = useObjectVisibility(ref);

  const promoDetails = useMemo(
    () => ({
      id: button_text,
      name: button_text,
      type: 'link',
      url: link,
      position: `${pageType}-${sliceIndex}-HeroImage`,
    }),
    [button_text, link, pageType, sliceIndex]
  );

  useEffect(() => {
    if (trackPromos && isVisible) {
      eePromoImpHandler(promoDetails);
    }
  }, [promoDetails, isVisible, trackPromos]);

  const handleClick = () => {
    setPromoDetailsCookie(promoDetails);
  };

  return (
    <div ref={ref}>
      {!!link ? (
        <BannerImage
          image={image}
          link={`/${locale}${link}`}
          trackPromos={trackPromos}
          pageType={pageType}
          sliceIndex={sliceIndex}
        />
      ) : (
        <BannerImage image={image} />
      )}
      <Container className={styles.contentContainer}>
        <Heading semanticLevel="h1" styleLevel="1" className={styles.heading}>
          {heading}
        </Heading>
        {content && <p>{content}</p>}
        {link && button_text && (
          <Button
            label={button_text}
            size="large"
            variant="primary"
            type="button"
            href={`/${locale}${link}`}
            handleClick={trackPromos ? () => handleClick() : undefined}
          />
        )}
      </Container>
    </div>
  );
};

export default HeroImage;
